import React, { useEffect, useState } from "react"
import * as yup from "yup"
import { Box, Typography } from "src/UILibrary"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { validateField } from "src/modules/validation"
import InputTextField from "src/components/atom/InputTextField"
import "suneditor/dist/css/suneditor.min.css"
import { HTMLEditor } from "./HTMLEditor"
import { PublishPanelItem } from "./PublishPanelItem"
import FileSelectField from "src/components/atom/FileSelectField"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"
import {
  NEWS_INTERVIEW_TYPES_STRING,
  NEWS_STATE,
  RESPONSE_CODE,
  SNACKBAR_TYPES,
} from "src/modules/const"
import { News, NewsService } from "src/openapi"
import { useLoginContext } from "src/hooks/useLoginContext"
import { useNavigate } from "react-router"
import { ShowProgress } from "src/states/showProgress"

interface Props {
  edit?: boolean
  item?: News
  closeDetail?: any
  pageType?: string
}
export const CreateNewsPageItem = ({ edit, item, closeDetail, pageType }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.news" })
  const { checkLoginStatus, accessToken } = useLoginContext()
  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [title, setTitle] = useState<string>(edit ? item?.title! : "")
  const [titleValidationMessage, setTitleValidationMessage] = useState<string>("")

  const [image, setImage] = useState<string>(edit ? item?.imageUrl! : "")
  const [imageValidationMessage, setImageValidationMessage] = useState<string>("")

  const [content, setContent] = useState<string>(edit ? item?.content! : "")
  const [contentValidationMessage, setContentValidationMessage] = useState<string>("")
  const navigate = useNavigate()

  const [, setShowToast] = useRecoilState(ShowToast)

  const onFileSelected = (url: string) => {
    setImage(url)
  }

  const handleHTMLChange = (content: string) => {
    setContent(content)
  }

  const handleBack = (category?: string) => {
    if (category) {
      if (category == NEWS_INTERVIEW_TYPES_STRING.NEWS) {
        navigate("/mypage/news/news")
      } else if (category == NEWS_INTERVIEW_TYPES_STRING.INTERVIEW) {
        navigate("/mypage/news/interview")
      } else {
        navigate("/mypage/news/list")
      }
    } else {
      if (pageType == NEWS_INTERVIEW_TYPES_STRING.NEWS) {
        navigate("/mypage/news/news")
      } else if (pageType == NEWS_INTERVIEW_TYPES_STRING.INTERVIEW) {
        navigate("/mypage/news/interview")
      } else {
        navigate("/mypage/news/list")
      }
    }
    navigate(0)
  }
  const handleSave = async (category: string, areaId: number) => {
    if (!(await validate())) {
      return
    }

    if (!category) {
      return
    }

    setShowProgress(true)
    NewsService.updateNews(accessToken, item?.id!, {
      type: category,
      content: content,
      title: title,
      imageUrl: image,
      isPublic: item?.isPublic,
      areaId: areaId,
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success"), type: SNACKBAR_TYPES.SUCCESS })
          //handleBack(category)
        } else {
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }
  const handlePublish = async (category: string, areaId: number) => {
    if (!(await validate())) {
      return
    }

    if (!category) {
      return
    }

    setShowProgress(true)
    NewsService.createNews(accessToken, {
      imageUrl: image,
      title: title,
      content: content,
      areaId: areaId,
      type: category,
      isPublic: true,
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
          setShowToast({ show: true, text: t("success"), type: SNACKBAR_TYPES.SUCCESS })
          handleBack(category)
        } else {
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handlePrivate = () => {
    if (!item?.id) return
    setShowProgress(true)
    NewsService.updateNews(accessToken, item?.id!, { isPublic: !item?.isPublic })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success"), type: SNACKBAR_TYPES.SUCCESS })
          //handleBack()
        } else {
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleDelete = () => {
    if (!item?.id) return
    setShowProgress(true)
    NewsService.deleteNews(accessToken, item?.id!)
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success"), type: SNACKBAR_TYPES.SUCCESS })
          handleBack()
        } else {
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const validate = async () => {
    let ret = true

    try {
      await validateField(
        yup.object().shape({
          title: yup.string().required(t("please_input_titile")),
        }),
        "title",
        title
      )
      setTitleValidationMessage("")
    } catch (err: any) {
      setTitleValidationMessage(err.message)
      ret = ret && false
    }

    if (image) {
      setImageValidationMessage("")
    } else {
      setImageValidationMessage(t("please_select_file"))
      ret = false
    }

    if (content) {
      setContentValidationMessage("")
    } else {
      setContentValidationMessage(t("please_add_content"))
      ret = false
    }

    return ret
  }

  useEffect(() => {})

  return (
    <Box
      sx={{
        ...(isTablet
          ? { height: "800px", pt: "0px", pl: "20px" }
          : { width: "100%", pl: "20px", pr: "20px", position: "absolute", top: 60, bottom: 120 }),
        overflowY: "scroll",
      }}
    >
      {edit ? (
        <Typography.FileFieldDetail
          sx={{
            fontFamily: "Noto Sans JP !important",
            my: "12px",
            ml: "0px",
            ":hover": { opacity: 0.8, cursor: "pointer" },
          }}
          onClick={() => {
            closeDetail()
          }}
        >
          {"＜　"}
          {t("page_back")}
        </Typography.FileFieldDetail>
      ) : (
        <></>
      )}

      <Box display={isTablet ? "flex" : "block"}>
        <Box width="100%">
          <InputTextField
            name={t("title")}
            required
            text={title}
            handleText={setTitle}
            sx={{ mt: "20px", mr: "10px" }}
            placeholder=""
            labelStyle={{ fontWeight: "700" }}
            validationMessage={titleValidationMessage}
          />

          <Box mt="40px">
            <FileSelectField
              required
              name={t("thumb_image")}
              handleChange={onFileSelected}
              file={image}
              validationMessage={imageValidationMessage}
              labelStyle={{
                fontFamily: "Noto Sans JP !important",
                fontSize: "16px",
                fontWeight: "700",
              }}
            />
          </Box>
          {/* <ImageUploader
            title={t("thumb_image")}
            sx={{ mt: "30px" }}
            onFileSelected={onFileSelected}
            labelStyle={{ fontWeight: "700" }}
            image={image}
            required
          /> */}
          <HTMLEditor
            title={t("content")}
            content={content}
            sx={{ mt: "30px" }}
            labelStyle={{ fontWeight: "700" }}
            required
            validationMessage={contentValidationMessage}
            handleChange={handleHTMLChange}
          />
        </Box>
        <Box sx={{ bgcolor: "#FAFAFA", py: isTablet ? "0px" : "20px" }}>
          <Box width={isTablet ? "230px" : "100%"} height="100%">
            <PublishPanelItem
              state={edit ? NEWS_STATE.EDIT : NEWS_STATE.CREATE}
              type={item?.type}
              area={item?.areaId}
              isPublic={item?.isPublic}
              handlePublish={handlePublish}
              handlePrivate={handlePrivate}
              handleDelete={handleDelete}
              handleSave={handleSave}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
